import Select from 'react-select'
import type { GroupBase, Props } from 'react-select'
import { selectStructuralStyles, selectTheme } from './Select.theme'

/**
 * Composes two react-select style objects instead of replacing keys.
 *
 * A plain spread would drop the base colours for every key the caller overrides
 * (`styles={{ container: ... }}` alone would un-theme the container). Here the
 * caller's function receives the themed result as its base, so per-instance
 * tweaks — width, z-index — stack on top of the shared palette.
 */
export const mergeSelectStyles = (base: any, extra: any): any => {
  if (!extra) {
    return base
  }

  const merged: any = { ...base }
  Object.keys(extra).forEach((key) => {
    const themed = base[key]
    const custom = extra[key]
    merged[key] = themed
      ? (styles: any, state: any) => custom(themed(styles, state), state)
      : custom
  })

  return merged
}

/**
 * Project-wide react-select. Use it instead of importing `react-select` directly.
 *
 * Recolouring goes through react-select's own `theme` prop (`selectTheme`), so a
 * dropdown is themed out of the box: no `useThemeMode()`, no styles prop, no
 * re-render on theme switch. Every react-select prop is forwarded, and `styles`
 * still works — it is layered on top of the shared palette rather than replacing
 * it. Pass `theme` explicitly only to override the palette itself.
 */
export function AppSelect<
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>
>({
  styles,
  classNamePrefix = 'select',
  theme = selectTheme,
  ...rest
}: Props<Option, IsMulti, Group>) {
  return (
    <Select<Option, IsMulti, Group>
      classNamePrefix={classNamePrefix}
      theme={theme}
      styles={mergeSelectStyles(selectStructuralStyles, styles)}
      {...rest}
    />
  )
}
